/**
 * BatchRestrictedScreen — Iter 191.
 *
 * Batch-scoped counterpart to <FrozenScreen />. Renders when a
 * student's enrollment for a specific batch has been restricted by
 * an admin. The student's account itself is NOT frozen — their
 * OTHER active batches remain fully accessible, and the screen
 * surfaces those as pivot options.
 *
 * Route guard usage:
 *   <BatchAccessGate batchId={id}> ... </BatchAccessGate>
 * Or standalone (when you already know the batch_id):
 *   <BatchRestrictedScreen batchId={id} />
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowRight, ShieldAlert, Home, Loader2, LifeBuoy, Mail, Phone, MessageCircle, Clock } from "lucide-react";
import { API } from "../contexts/AuthContext";

export default function BatchRestrictedScreen({ batchId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    axios.get(`${API}/me/batches/${batchId}/access`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ restricted: true }))
      .finally(() => setLoading(false));
  }, [batchId]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="batch-restricted-loading">
        <Loader2 size={22} className="animate-spin" style={{ color: "#0f172a" }} />
      </div>
    );
  }

  const batchName = info?.batch?.name || "this batch";
  const others = info?.other_active_batches || [];

  return (
    <div className="min-h-[80vh] w-full flex items-start justify-center px-4 md:px-10 py-8"
         data-testid="batch-restricted-screen">
      <div className="w-full max-w-[720px]">
        {/* Hero */}
        <div
          className="rounded-[18px] p-6 md:p-7 mb-5 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #fff7ed, #fef2f2)",
            border: "1px solid #fecaca",
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "rgba(220,38,38,0.10)", border: "1.5px solid rgba(220,38,38,0.35)" }}
            >
              <Lock size={20} style={{ color: "#b91c1c" }} strokeWidth={2.3} />
            </div>
            <div className="min-w-0">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: "#b91c1c" }}>
                Batch access restricted
              </div>
              <h1 className="text-[22px] md:text-[26px] font-black leading-tight text-slate-900">
                Your access to <span style={{ color: "#b91c1c" }}>{batchName}</span> has been restricted
              </h1>
            </div>
          </div>
          <p className="text-[13.5px] leading-relaxed text-slate-700">
            An admin has temporarily paused your access to this batch — all study content, MCQs, flashcards,
            discussions, and mentor tools tied to <strong>{batchName}</strong> are locked until an admin
            restores access. Your other batches are not affected.
          </p>
        </div>

        {/* Personalized reason */}
        {info?.reason && (
          <div
            className="rounded-[14px] p-4 md:p-5 mb-5"
            style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}
            data-testid="batch-restricted-reason"
          >
            <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] mb-1.5"
                 style={{ color: "#b45309" }}>
              Why your access was restricted
            </div>
            <p className="text-[13px] leading-relaxed text-amber-900 whitespace-pre-line">
              {info.reason}
            </p>
          </div>
        )}

        {/* Reassurance */}
        <div
          className="rounded-[14px] p-4 md:p-5 mb-5 flex items-start gap-3"
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
        >
          <ShieldAlert size={16} className="shrink-0 mt-0.5" style={{ color: "#475569" }} />
          <p className="text-[13px] leading-relaxed text-slate-600">
            <strong className="text-slate-900">Your data is safe.</strong>{" "}
            All progress, saved MCQs, flashcards and streak history for this batch are preserved. The moment
            an admin lifts the restriction, you&apos;ll return exactly where you left off.
          </p>
        </div>

        {/* Other active batches */}
        {others.length > 0 && (
          <div
            className="rounded-[14px] p-4 md:p-5 mb-5"
            style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}
            data-testid="batch-restricted-other-batches"
          >
            <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-3">
              You still have access to
            </div>
            <div className="grid grid-cols-1 gap-2">
              {others.map((b) => (
                <Link
                  key={b.id}
                  to={`/student/study?batch=${encodeURIComponent(b.id)}`}
                  className="flex items-center justify-between p-3 rounded-[10px] transition"
                  style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
                  data-testid={`batch-restricted-other-batch-${b.id}`}
                >
                  <span className="font-semibold text-[13.5px] text-slate-900">{b.name}</span>
                  <ArrowRight size={14} style={{ color: "#0f172a" }} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Utility row */}
        <div className="flex items-center gap-3 flex-wrap mb-5">
          <Link
            to="/student"
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-[10px] text-[13px] font-semibold transition"
            style={{ background: "#0f172a", color: "#ffffff" }}
            data-testid="batch-restricted-home"
          >
            <Home size={14} /> Back to dashboard
          </Link>
        </div>

        {/* Help desk — always visible, no expand/collapse.
            Iter 193.8 — replaced Tailwind text-white/text-slate-* classes
            with explicit inline colors to guarantee contrast on the dark
            navy card. Some Tailwind builds purge text-* utilities that
            aren't seen in JSX, leaving text unreadable in production. */}
        {info?.help_desk && (
          <div
            className="rounded-[16px] p-5 md:p-6"
            style={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              border: "1px solid rgba(148,163,184,0.30)",
              boxShadow: "0 18px 42px -20px rgba(15,23,42,0.55)",
            }}
            data-testid="batch-restricted-help-desk"
          >
            <div className="flex items-center gap-2.5 mb-4">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: "rgba(96,165,250,0.20)", border: "1.5px solid rgba(96,165,250,0.55)" }}
              >
                <LifeBuoy size={16} style={{ color: "#93c5fd" }} strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "#93c5fd" }}>
                  LW Help Desk
                </div>
                <div className="text-[15px] font-black leading-tight" style={{ color: "#ffffff" }}>
                  Contact us to restore access
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {info.help_desk.email && (
                <a
                  href={`mailto:${info.help_desk.email}?subject=${encodeURIComponent(`Batch access restricted — ${batchName}`)}`}
                  className="flex items-center gap-2.5 h-11 px-3.5 rounded-[10px] transition"
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(148,163,184,0.35)" }}
                  data-testid="help-desk-email"
                >
                  <Mail size={14} className="shrink-0" style={{ color: "#93c5fd" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold leading-none mb-0.5" style={{ color: "#cbd5e1" }}>Email</div>
                    <div className="text-[12.5px] font-semibold truncate" style={{ color: "#ffffff" }}>{info.help_desk.email}</div>
                  </div>
                </a>
              )}
              {info.help_desk.phone && (
                <a
                  href={`tel:${info.help_desk.phone}`}
                  className="flex items-center gap-2.5 h-11 px-3.5 rounded-[10px] transition"
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(148,163,184,0.35)" }}
                  data-testid="help-desk-phone"
                >
                  <Phone size={14} className="shrink-0" style={{ color: "#93c5fd" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold leading-none mb-0.5" style={{ color: "#cbd5e1" }}>Phone</div>
                    <div className="text-[12.5px] font-semibold truncate" style={{ color: "#ffffff" }}>{info.help_desk.phone}</div>
                  </div>
                </a>
              )}
              {info.help_desk.whatsapp && (
                <a
                  href={`https://wa.me/${(info.help_desk.whatsapp || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi, my access to "${batchName}" has been restricted. Please help me restore access.`)}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-2.5 h-11 px-3.5 rounded-[10px] transition"
                  style={{ background: "rgba(34,197,94,0.18)", border: "1px solid rgba(34,197,94,0.55)" }}
                  data-testid="help-desk-whatsapp"
                >
                  <MessageCircle size={14} className="shrink-0" style={{ color: "#4ade80" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold leading-none mb-0.5" style={{ color: "#86efac" }}>WhatsApp</div>
                    <div className="text-[12.5px] font-semibold truncate" style={{ color: "#ffffff" }}>{info.help_desk.whatsapp}</div>
                  </div>
                </a>
              )}
              {info.help_desk.hours && (
                <div
                  className="flex items-center gap-2.5 h-11 px-3.5 rounded-[10px]"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(148,163,184,0.25)" }}
                >
                  <Clock size={14} className="shrink-0" style={{ color: "#93c5fd" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold leading-none mb-0.5" style={{ color: "#cbd5e1" }}>Hours</div>
                    <div className="text-[12.5px] font-semibold truncate" style={{ color: "#ffffff" }}>{info.help_desk.hours}</div>
                  </div>
                </div>
              )}
            </div>
            {info.help_desk.instructions && (
              <p className="mt-4 text-[12px] leading-relaxed" style={{ color: "#e2e8f0" }}>
                {info.help_desk.instructions}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
